import { decodeImage, Fit, Layout, Rive } from '@rive-app/canvas-lite'
import { onCleanup, onMount } from 'solid-js'
import stampSvg from '~/assets/figma/stamp.svg'
import rewardRiv from '~/assets/rive/reward.riv'
import { useLocalizedTreeName } from '~/hooks/use-localized-tree-name'
import { getRewardTreeAssets } from '~/lib/reward-tree-assets'
import { m } from '~/paraglide/messages'
import { useActivityTokenBalance } from '~/resources/activity-token-balance'
import { CurrencyPill } from './top-nav'

const SM = 'State Machine 1'

async function loadDecodedImage(url: string) {
  const res = await fetch(url)
  const buf = await res.arrayBuffer()
  return decodeImage(new Uint8Array(buf))
}

export function PlusTreeRewardPage(props: {
  treeGid: number
  numRewardType: 1 | 2
  onClaim: () => void
}) {
  const treeName = useLocalizedTreeName(() => props.treeGid)
  const balanceQuery = useActivityTokenBalance()

  let canvas: HTMLCanvasElement | undefined
  let rive: Rive | undefined
  let onResize: (() => void) | undefined
  const refs: { unref(): void }[] = []

  onMount(() => {
    if (!canvas) return
    const { full, blank } = getRewardTreeAssets(props.treeGid)

    rive = new Rive({
      canvas,
      src: rewardRiv,
      artboard: 'Plus Tree',
      stateMachines: [SM],
      autoBind: true,
      autoplay: true,
      shouldDisableRiveListeners: true,
      layout: new Layout({ fit: Fit.Contain }),
      onLoad: async () => {
        if (!rive) return
        rive.resizeDrawingSurfaceToCanvas()
        onResize = () => rive?.resizeDrawingSurfaceToCanvas()
        window.addEventListener('resize', onResize)

        const vmi = rive.viewModelInstance
        if (!vmi) return

        const num = vmi.number('numRewardType')
        if (num) num.value = props.numRewardType

        if (full) {
          const img = await loadDecodedImage(full)
          vmi.image('imgReward')!.value = img
          refs.push(img)
        }
        if (blank) {
          const img = await loadDecodedImage(blank)
          vmi.image('imgRewardBlank')!.value = img
          refs.push(img)
        }
      },
    })

    onCleanup(() => {
      rive?.cleanup()
      if (onResize) window.removeEventListener('resize', onResize)
      for (const ref of refs) ref.unref()
    })
  })

  return (
    <div
      class="fixed inset-0 z-[60] flex flex-col pt-[100px]"
      style={{
        background:
          'radial-gradient(106.67% 55.25% at 50.13% 44.75%, #FFE8CB 24.52%, #EFA4B6 55.77%, #926FB0 100%)',
      }}
    >
      <div
        class="pointer-events-none fixed inset-x-0 top-0 z-10 flex justify-end px-2"
        style={{ 'padding-top': 'max(var(--safe-area-top, 0px), 36px)' }}
      >
        <div class="pointer-events-auto py-2">
          <CurrencyPill
            icon={stampSvg}
            value={balanceQuery.data?.balance ?? 0}
          />
        </div>
      </div>

      <div class="flex flex-1 flex-col gap-6 px-6 pt-10 text-center">
        <h1 class="text-xl font-semibold text-white">{m.claimed_title()}</h1>

        <div class="relative h-[50vh]">
          <canvas
            ref={(el) => (canvas = el)}
            class="absolute left-1/2 top-0 h-full w-screen -translate-x-1/2"
            aria-hidden="true"
          />
        </div>

        <p class="mt-6 text-[26px] font-bold text-white">{treeName()}</p>
      </div>

      <div class="flex flex-col px-6 pb-12 pt-4">
        <button
          type="button"
          class="mx-auto h-[52px] w-full max-w-[640px] rounded-xl bg-white px-4 font-bold text-[#176361]"
          onClick={props.onClaim}
        >
          {m.cart_claim_active()}
        </button>
      </div>
    </div>
  )
}
